import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AlertTriangle } from 'lucide-react';

interface Church {
  id: number;
  name: string;
}

interface ChurchChangeConfirmationModalProps {
  show: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  oldChurchId: number | null;
  newChurchId: number | null;
  churches: Church[];
  season: string;
  liturgicalYear: string;
  oldServiceCount: number;
  newServiceCount: number;
}

const ChurchChangeConfirmationModal: React.FC<ChurchChangeConfirmationModalProps> = ({
  show,
  onConfirm,
  onCancel,
  oldChurchId,
  newChurchId,
  churches,
  season,
  liturgicalYear,
  oldServiceCount,
  newServiceCount
}) => {
  const getChurchName = (churchId: number | null): string => {
    if (churchId === null) return 'All Churches';
    return churches.find(c => c.id === churchId)?.name || 'Unknown Church';
  };

  const oldChurchName = getChurchName(oldChurchId);
  const newChurchName = getChurchName(newChurchId);

  return (
    <Modal
      show={show}
      onHide={onCancel}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>
          <AlertTriangle size={24} className="me-2 text-warning" />
          Confirm Church Assignment Change
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="alert alert-warning mb-3">
          <strong>Warning:</strong> You are changing the church assignment for this template.
        </div>

        <div className="mb-3">
          <p><strong>Template:</strong> {season} {liturgicalYear.replace('_', ' ')}</p>
          <p className="mb-1"><strong>Old Assignment:</strong> {oldChurchName}</p>
          <p className="mb-3"><strong>New Assignment:</strong> {newChurchName}</p>
        </div>

        <div className="mb-3">
          <h6 className="mb-2">This will:</h6>
          <ul className="mb-0">
            <li>
              <strong>Remove</strong> the old template and its music from {oldChurchName} services
              {oldServiceCount > 0 && (
                <span className="text-muted"> (affects {oldServiceCount} service{oldServiceCount !== 1 ? 's' : ''})</span>
              )}
            </li>
            <li>
              <strong>Create</strong> a new template and apply its music to {newChurchName} services
              {newServiceCount > 0 && (
                <span className="text-muted"> (affects {newServiceCount} service{newServiceCount !== 1 ? 's' : ''})</span>
              )}
            </li>
          </ul>
        </div>

        <div className="alert alert-info mb-0">
          <small>
            <strong>Note:</strong> Service music already manually assigned to services will NOT be affected.
            Only template-assigned music will be updated.
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="warning" onClick={onConfirm}>
          Continue with Change
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChurchChangeConfirmationModal;
